/**
 * XMsg framing + per-message encode/decode — port of mpshared/xmsg.h + messages.{h,cpp}.
 *
 * Frame: [u16 size big-endian][u8 id][payload], size INCLUDES the 2-byte size field.
 * (xmsg.h:16-18, ByteBuffer big-endian.) Every encoder reproduces the exact bytes the C++
 * `XMsg*::ToBuffer` produces; every decoder is its inverse. Validated byte-for-byte against
 * the golden vectors in cloudflare/test/vectors/mp/.
 *
 * Design: decode(bytes) -> typed message object {id, ...fields}; encode(msg) -> Uint8Array.
 * Round-trip invariant: encode(decode(b)) === b for every captured frame.
 */
import { ByteReader, ByteWriter } from './bytebuffer.ts';
import { XMSG, XMSG_FIXED } from './constants.ts';

export interface FrameHeader {
  size: number; // total frame size incl. the size field
  id: number; // XMSG_*
}

/** Read just the frame header (size,id) without consuming payload semantics. */
export function readHeader(buf: Uint8Array): FrameHeader {
  const r = new ByteReader(buf);
  const size = r.readWord();
  const id = r.readByte();
  return { size, id };
}

// ── Typed messages ───────────────────────────────────────────────────────────
// Each variant carries its id + decoded fields. Unknown/opaque payloads keep raw bytes
// so round-trip still holds for message types we haven't field-decoded yet.

export type XMsgMessage =
  | { id: number; kind: 'zero' } // XMsg0: [size][id], no payload
  | { id: number; kind: 'dword1'; dw0: number } // XMsg1
  | { id: number; kind: 'dword2'; dw0: number; dw1: number } // XMsg2
  | { id: number; kind: 'dword3'; dw0: number; dw1: number; dw2: number } // XMsg3
  | { id: number; kind: 'strings'; strings: string[] } // XMsgS* (variable length-prefixed)
  | { id: number; kind: 'dword_string'; dw0: number; s0: string } // XMsgDS
  // LOBBYADDROOM: bespoke fixed layout [char room[32]][u32 roomid][u32 priv][u32 cPlayers][u32 cGames]
  | {
      id: number;
      kind: 'lobbyaddroom';
      room: string;
      roomid: number;
      priv: number;
      cPlayers: number;
      cGames: number;
    }
  | { id: number; kind: 'raw'; payload: Uint8Array }; // opaque (field-decode TODO)

// Which ids are pure XMsg0/1/2/3 (fixed-size, no strings). From messages.h typedefs.
const ZERO_IDS = new Set<number>([
  XMSG.LOBBYJOIN,
  XMSG.LOBBYLEAVE,
  XMSG.ROOMSTATUSCOMPLETE,
  XMSG.ECHO,
  XMSG.SIGNOUT,
  XMSG.DISCONNECTSHAREDACCOUNTS,
]); // XMsg0 client→server
const DWORD1_IDS = new Set<number>([
  XMSG.LOGINRESULT,
  XMSG.LOBBYJOINRESULT,
  XMSG.LOBBYLEAVERESULT,
  XMSG.LOBBYREMOVEROOM,
  XMSG.LOBBYLURKERCOUNT,
  XMSG.ROOMJOINRESULT,
  XMSG.GAMEJOINRESULT,
  XMSG.ROOMLEAVE,
  XMSG.ROOMLEAVERESULT,
  XMSG.ROOMCANJOINGAME, // XMsg1 [gameid] client→server (join-game pre-check)
  XMSG.GAMEKILLED, // XMsg1 [gameid] server→client (DropGame: lag-kill / kill-vote)
]);
const DWORD2_IDS = new Set<number>([
  XMSG.HANDSHAKE,
  XMSG.HANDSHAKERESULT,
  XMSG.ROOMREMOVEGAME,
  XMSG.GAMEJOIN,
  XMSG.GAMEUPDATEALLIES, // XMsg2 [side, allies] client→server
  XMSG.LOBBYCREATEROOMRESULT, // XMsg2 [result, roomid] server→client (messages.h:161)
]);
const DWORD3_IDS = new Set<number>([XMSG.LOBBYUPDATEROOM]); // XMsg3
const STRING_IDS = new Set<number>([
  XMSG.LOGIN,
  XMSG.ROOMADDPLAYER,
  XMSG.LOBBYCREATEROOM, // LobbyCreateRoom = XMsgS2 [name, password]
  // chat: XMsgS1 (send: [chat]) / XMsgS2 (receive: [name, chat]) — all length-prefixed strings
  XMSG.ROOMSENDCHAT,
  XMSG.ROOMRECEIVECHAT,
  XMSG.GAMESENDCHAT,
  XMSG.GAMERECEIVECHAT,
  XMSG.LOBBYRECEIVECHAT,
]);
const DWORD_STRING_IDS = new Set<number>([
  XMSG.ROOMJOIN,
  XMSG.ROOMREMOVEPLAYER,
  XMSG.LOBBYCANJOINROOM,
]); // XMsgDS

/** Decode a framed XMsg into a typed message. */
export function decode(buf: Uint8Array): XMsgMessage {
  const r = new ByteReader(buf);
  const size = r.readWord();
  if (size !== buf.length) {
    throw new Error(`frame size ${size} != buffer length ${buf.length}`);
  }
  const id = r.readByte();

  if (ZERO_IDS.has(id)) return { id, kind: 'zero' };
  if (DWORD1_IDS.has(id)) return { id, kind: 'dword1', dw0: r.readDword() };
  if (DWORD2_IDS.has(id)) return { id, kind: 'dword2', dw0: r.readDword(), dw1: r.readDword() };
  if (DWORD3_IDS.has(id)) {
    return { id, kind: 'dword3', dw0: r.readDword(), dw1: r.readDword(), dw2: r.readDword() };
  }
  if (id === XMSG.LOBBYADDROOM) {
    return {
      id,
      kind: 'lobbyaddroom',
      room: r.readFixedString(32),
      roomid: r.readDword(),
      priv: r.readDword(),
      cPlayers: r.readDword(),
      cGames: r.readDword(),
    };
  }
  if (DWORD_STRING_IDS.has(id)) {
    return { id, kind: 'dword_string', dw0: r.readDword(), s0: r.readPrefixedString() };
  }
  if (STRING_IDS.has(id)) {
    const strings: string[] = [];
    while (r.remaining > 0) strings.push(r.readPrefixedString());
    return { id, kind: 'strings', strings };
  }
  // Opaque: keep the raw payload (everything after [size][id]) for faithful round-trip.
  return { id, kind: 'raw', payload: buf.slice(XMSG_FIXED) };
}

/** Encode a typed message back to exact wire bytes. */
export function encode(msg: XMsgMessage): Uint8Array {
  const body = new ByteWriter();
  switch (msg.kind) {
    case 'zero':
      break;
    case 'dword1':
      body.writeDword(msg.dw0);
      break;
    case 'dword2':
      body.writeDword(msg.dw0);
      body.writeDword(msg.dw1);
      break;
    case 'dword3':
      body.writeDword(msg.dw0);
      body.writeDword(msg.dw1);
      body.writeDword(msg.dw2);
      break;
    case 'lobbyaddroom':
      body.writeFixedString(msg.room, 32); // kcbRoomname=32, zero-padded fixed field
      body.writeDword(msg.roomid);
      body.writeDword(msg.priv);
      body.writeDword(msg.cPlayers);
      body.writeDword(msg.cGames);
      break;
    case 'dword_string':
      body.writeDword(msg.dw0);
      body.writeWord(msg.s0.length + 1); // cb incl NUL
      body.writeString(msg.s0, true);
      break;
    case 'strings':
      for (const s of msg.strings) {
        body.writeWord(s.length + 1);
        body.writeString(s, true);
      }
      break;
    case 'raw':
      body.writeBytes(msg.payload);
      break;
  }
  const size = XMSG_FIXED + body.length;
  const out = new ByteWriter();
  out.writeWord(size);
  out.writeByte(msg.id);
  out.writeBytes(body.toUint8Array());
  return out.toUint8Array();
}
